"use client";

import React, { useEffect } from "react";
import { TermsContent, TermsCardChildItems } from "@/.storyblok/types/319751/storyblok-components";
import { StoryblokRichtext } from "@/.storyblok/types/storyblok";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { renderRichText } from "@/lib/richText";
import Link from "next/link";
import ServicesAgreementStandard from "./ServicesAgreementStandard";
import ServicesAgreementServiceSpecific from "./ServicesAgreementServiceSpecific";

interface TermsContentProps {
  blok?: TermsContent & SbBlokData;
}

const TermsContentComponent: React.FC<TermsContentProps> = ({ blok }) => {
  useEffect(() => {
    const handleScroll = () => {
      const tocLinks = document.querySelectorAll<HTMLAnchorElement>('.terms-conditions-toc-links ol > li > a');

      tocLinks.forEach((link) => {
        const href = link.getAttribute("href");
        if (!href) return;

        try {
          const id = href.substring(1);
          const target = document.getElementById(id);
          if (!target) return;

          const coords = target.getBoundingClientRect();

          if (coords.top <= 0 && coords.bottom >= 0) {
            link.classList.add("active");
          } else {
            link.classList.remove("active");
          }
        } catch (error) {
          console.warn(`Error handling scroll for link ${href}:`, error);
        }
      });
    };

    if (document.querySelector('.terms-conditions-toc')) {
      document.addEventListener('scroll', handleScroll);
    }

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!blok) return null;

  if (!blok.all_terms || blok.all_terms.length === 0) {
    return (
      <div {...storyblokEditable(blok)}>
        <ServicesAgreementStandard />
        <hr className="my-0" />
        <ServicesAgreementServiceSpecific />
      </div>
    );
  }

  const createId = (title: string | undefined): string => {
    if (!title) return "";
    return title.toLowerCase().replace(/[\s&]/g, '_').replace(/[\.]/g, '');
  };

  const renderStoryblokContent = (content: StoryblokRichtext | undefined): JSX.Element | null => {
    if (!content) return null;
    return (
      <div dangerouslySetInnerHTML={{ __html: renderRichText(content) as string }} />
    );
  };

  const renderTitle = (title: string | StoryblokRichtext | undefined): JSX.Element | string | null => {
    if (!title) return null;
    if (typeof title === 'string') return title;
    return <div dangerouslySetInnerHTML={{ __html: renderRichText(title) as string }} />;
  };

  const partnerTermsSection = blok.all_terms.find(section => section.terms_type === "partner_terms");

  if (partnerTermsSection) {
    return (
      <div {...storyblokEditable(blok)}>
        <div className="terms py-3 py-md-4 py-xl-5" id="service_specific_terms">
          <section className="container">
            <div className="row justify-content-center">
              {/* Table of contents */}
              <div className="col-md-10 col-lg-8 col-xxl-6 mb-5 mb-md-0">
                <ol className="list-unstyled terms-conditions-toc">
                  <li className="bg-white rounded-3 p-2 shadow mb-0 terms-conditions-toc-links">
                    <div className="p-2 pt-0">
                      <ol className="py-1">
                        {partnerTermsSection.terms_items && partnerTermsSection.terms_items.map((item) => (
                          <li key={item._uid}>
                            <Link
                              className="fw-bold text-primary"
                              href={`#${createId(item.title)}`}
                            >
                              {item.title}
                            </Link>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </li>
                </ol>
              </div>

              {/* Terms Content */}
              <div className="col-md-14 col-lg-16 col-xxl-18 main terms-conditions-toc-content">
                {/* Partner terms intro sections */}
                {partnerTermsSection.terms_intro && partnerTermsSection.terms_intro.map((intro) => (
                  <div key={intro._uid}>
                    {intro.title && <h2 className="h4">{intro.title}</h2>}
                    {renderStoryblokContent(intro.content)}
                  </div>
                ))}

                {/* Partner terms main items */}
                {partnerTermsSection.terms_items && partnerTermsSection.terms_items.length > 0 && (
                  <ol className="decimal-list">
                    {partnerTermsSection.terms_items.map((item) => (
                      <li key={item._uid} id={createId(item.title)}>
                        <h3 className="lh-base lead fw-bold mb-2 text-uppercase">{item.title}</h3>
                        {renderStoryblokContent(item.description as StoryblokRichtext)}
                        {renderStoryblokContent(item.content as StoryblokRichtext)}

                        {item.items && item.items.length > 0 && (
                          <ol className="decimal-list">
                            {item.items.map((subItem: TermsCardChildItems) => (
                              <li key={subItem._uid}>
                                <div>
                                  {renderTitle(subItem.title)}
                                </div>
                                {renderStoryblokContent(subItem.content)}
                              </li>
                            ))}
                          </ol>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  const standardTermsSection = blok.all_terms.find(section => section.terms_type === "standard_terms");
  const serviceSpecificTermsSection = blok.all_terms.find(section => section.terms_type === "service_specific_terms");

  return (
    <div {...storyblokEditable(blok)}>
      {standardTermsSection && (
        <div className="terms py-3 py-md-4 py-xl-5 mb-5" id="standard_terms">
          <section className="container mb-5">
            <h2 className="mb-3 mb-xl-5 text-center">
              {standardTermsSection.title &&
                <>
                  {standardTermsSection.title.split(' ').slice(0, 2).join(' ')}
                  <span className="d-block text-primary">
                    {standardTermsSection.title.split(' ').slice(2).join(' ')}
                  </span>
                </>
              }
            </h2>

            <div className="row justify-content-center">
              {/* Table of contents */}
              <div className="col-md-10 col-lg-8 col-xxl-6 mb-5 mb-md-0">
                <ol className="list-unstyled terms-conditions-toc mb-0">
                  <li className="bg-white p-2 rounded-3 shadow mb-3">
                    <h3 className="lh-base lead fw-bold p-2 mb-0">
                      <i className="fas fa-chevron-right me-1" />
                      Standard Terms
                    </h3>
                  </li>
                  <li className="bg-white p-2 rounded-3 shadow">
                    <h3 className="lh-base lead fw-bold p-2 mb-0">
                      <Link href="#service_specific_terms">
                        <i className="fas fa-chevron-down me-1" />
                        Service-Specific Terms
                      </Link>
                    </h3>
                  </li>
                </ol>
              </div>
              {/* Terms content */}
              <div className="col-md-14 col-lg-16 col-xxl-18 main terms-conditions-toc-content">
                <div className="mb-4">
                  {/* Intro section */}
                  {standardTermsSection.terms_intro && standardTermsSection.terms_intro.map((intro) => (
                    <div key={intro._uid}>
                      {intro.title && <h3 className="h6 fw-bold text-uppercase">{intro.title}</h3>}
                      {intro.list_type === "upper_alpha" ? (
                        <div className="upper-alpha-list-container">
                          {renderStoryblokContent(intro.content)}
                        </div>
                      ) : intro.list_type === "decimal" ? (
                        <div className="mb-3">
                          {renderStoryblokContent(intro.content)}
                        </div>
                      ) : (
                        renderStoryblokContent(intro.content)
                      )}
                    </div>
                  ))}

                  {/* Main terms items */}
                  {standardTermsSection.terms_items && standardTermsSection.terms_items.length > 0 && (
                    <ol className="decimal-list">
                      {standardTermsSection.terms_items.map((item) => (
                        <li key={item._uid} id={createId(item.title)}>
                          <h4 className="lh-base lead fw-bold mb-2 text-uppercase">{item.title}</h4>
                          {renderStoryblokContent(item.description as StoryblokRichtext)}
                          {item.items && item.items.length > 0 && (
                            <ol className="decimal-list">
                              {item.items.map((subItem: TermsCardChildItems) => (
                                <li key={subItem._uid}>
                                  <div className="mb-0">
                                    {renderTitle(subItem.title)}
                                  </div>
                                  {renderStoryblokContent(subItem.content)}
                                </li>
                              ))}
                            </ol>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {standardTermsSection && serviceSpecificTermsSection && <hr className="my-0" />}

      {serviceSpecificTermsSection && (
        <div className="terms py-3 py-md-4 py-xl-5" id="service_specific_terms">
          <section className="container">
            <h2 className="mb-3 mb-xl-5 text-center">
              {serviceSpecificTermsSection.title &&
                <>
                  {serviceSpecificTermsSection.title.split(' ').slice(0, 2).join(' ')}
                  <span className="d-block text-primary">
                    {serviceSpecificTermsSection.title.split(' ').slice(2).join(' ')}
                  </span>
                </>
              }
            </h2>
            <div className="row justify-content-center">
              {/* Table of contents */}
              <div className="col-md-10 col-lg-8 col-xxl-6 mb-5 mb-md-0">
                <ol className="list-unstyled terms-conditions-toc">
                  <li className="bg-white rounded-3 p-2 shadow mb-3">
                    <h3 className="lh-base lead fw-bold p-2 mb-0">
                      <Link href="#standard_terms">
                        <i className="fas fa-chevron-up me-1" />
                        Standard Terms
                      </Link>
                    </h3>
                  </li>
                  <li className="bg-white rounded-3 p-2 shadow mb-0 terms-conditions-toc-links">
                    <h3 className="lh-base lead fw-bold p-2 mb-0">
                      <i className="fas fa-chevron-right me-1" />
                      Service-Specific Terms
                    </h3>
                    {serviceSpecificTermsSection.terms_items && serviceSpecificTermsSection.terms_items.length > 0 && (
                      <div className="p-2 pt-0">
                        <ol className="py-1">
                          {serviceSpecificTermsSection.terms_items.map((item) => (
                            <li key={item._uid}>
                              <Link
                                className="fw-bold text-primary"
                                href={`#${createId(item.title)}`}
                              >
                                {item.title}
                              </Link>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </li>
                </ol>
              </div>
              {/* Terms content */}
              <div className="col-md-14 col-lg-16 col-xxl-18 main terms-conditions-toc-content">
                <div className="mb-4">
                  {/* Service specific intro */}
                  {serviceSpecificTermsSection.terms_intro && serviceSpecificTermsSection.terms_intro.map((intro) => (
                    <div key={intro._uid}>
                      {renderStoryblokContent(intro.content)}
                    </div>
                  ))}

                  {/* Service specific items */}
                  {serviceSpecificTermsSection.terms_items && serviceSpecificTermsSection.terms_items.length > 0 && (
                    <ol className="decimal-list">
                      {serviceSpecificTermsSection.terms_items.map((item) => (
                        <li key={item._uid} id={createId(item.title)}>
                          <h3 className="h6 lead fw-bold text-uppercase">{item.title}</h3>
                          {renderStoryblokContent(item.description)}
                          {item.items && item.items.length > 0 && (
                            <ol className="upper-alpha-list">
                              {item.items.map((subItem: TermsCardChildItems) => (
                                <li key={subItem._uid}>
                                  <div className="h6 lead fw-bold text-uppercase">
                                    {renderTitle(subItem.title)}
                                  </div>
                                  {renderStoryblokContent(subItem.content)}
                                </li>
                              ))}
                            </ol>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default TermsContentComponent;